<template>
  <transition name="fade" mode="out-in">
    <div class="col-span-full flex flex-col gap-8 md:col-span-7">
      <h1 class="text-t2">Felanmälan</h1>
      <div class="w-full mx-auto px-[20px] py-[16px] bg-[#fff6cc] mb-[30px] rounded-[5px]" v-if="!!webapp_config.hide_errorreport" >
        <h2 class="text-t4 py-[8px]">{{ webapp_config.hide_errorreport_title }}</h2>
        <p v-html="webapp_config.hide_errorreport_text.replace(/\\r\\n/g, '<br />')"></p>
      </div>
      <Box v-else-if="notPremiseCompany" class="flex flex-col gap-4">
        <h2 v-if="!isLoading" class="text-t4">Anmäl ett fel</h2>

        <CardLoaderSmall v-if="isLoading" class="flex-1 flex-col !p-0 !ring-0" />

        <div v-else>
          <p class="text-b1">
            Du kan skapa en ny felanmälan direkt här på Mina sidor och sedan följa dess status medan
            den behandlas.
          </p>
          <p class="text-b1 bg-primary-10 px-4 py-2 mt-3 mb-5 rounded-[4px]">
            Om din felanmälan gäller akut ärende med fara för person eller egendom är det viktigt att ni kontaktar oss direkt vardagar mellan 07.00-16.00 på telefon 026-17 27 00.
            <br />Övrig tid ringer du 020-37 00 37.
          </p>
          <Button size="large" :to="{ path: '/felanmalan/skapa' }" class="w-fit" @click="mtm">
            Anmäl ett fel
          </Button>
        </div>
      </Box>

      <Box class="flex flex-col gap-8">
        <h2 v-if="!isLoading" class="text-t4">Mina felanmälningar</h2>

        <CardLoaderSmall v-if="isLoading" class="flex-1 flex-col !p-0 !ring-0" />

        <div v-else class="flex flex-col gap-12">
          <div class="flex flex-col gap-4">
            <h3 class="text-t6">Pågående ärenden</h3>
              <div class="flex flex-col gap-6 md:gap-8">
                <WorkorderItem v-for="item in getErrorReportsList.workorders" :item="item" />
              </div>

              <p v-if="!getErrorReportsList?.workorders?.length">
                Du har inte några pågående ärenden
              </p>
          </div>
          <div class="flex flex-col gap-4" v-if="getArchivedErrorReportsList?.workorders?.length">
            <div class="flex items-center justify-between">
              <h3 class="text-t6">Avslutade ärenden</h3>
            </div>
            <div class="flex flex-col gap-6 md:gap-8">
              <WorkorderItem
                v-for="(item, index) in displayedArchivedWorkorders"
                :key="item.code"
                :item="item"
                :isArchived="true"
              />
            </div>
            <div class="flex items-center justify-center">
              <Button v-if="getArchivedErrorReportsList.workorders.length > 3" variant="tertiary" @click="showAllArchived = !showAllArchived">
                {{ showAllArchived ? 'Visa färre' : `Visa tidigare ärenden` }}
                <template #icon-after>
                  <IconChevronDown :class="{ 'rotate-180': showAllArchived }" />
                </template>
              </Button>
            </div>
          </div>
        </div>
      </Box>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import Loader from '@/components/ui/loader';
import AppendableList from '@/components/ui/appendable-list';
import SearchableInput from '@/components/ui/searchableinput';
import { uniqBy, filter } from 'lodash';
import Box from '@/components/ui/box';
import Button from '@/components/ui/button';
import WorkorderItem from '@/pages/error-reports/workorder-item';
import CardLoaderSmall from '@/components/loader/CardLoaderSmall';
import IconChevronDown from '@/components/icons/IconChevronDown';
import { dataLayerPush } from '@/helpers/datalayer';
export default {
  name: 'ErrorReports',
  components: {
    Loader,
    AppendableList,
    SearchableInput,
    Box,
    Button,
    WorkorderItem,
    CardLoaderSmall,
    IconChevronDown,
  },
  data() {
    return {
      addressFilter: '',
      archivedAddressFilter: '',
      loaded: [],
      closed_txt: '',
      isLoading: true,
      showAllArchived: false,
      // testAlwaysLoading: true,
    };
  },
  computed: {
    ...mapGetters({
      webapp_config: 'webapp_config',
      profile: 'profile',
      getErrorReportsList: 'getErrorReportsList',
      getArchivedErrorReportsList: 'getArchivedErrorReportsList',
      notPremiseCompany: 'notPremiseCompany',
      contracts: 'contracts'
    }),

    isCompany() {
      return this.$store.getters.claims.some((el) => el == 'company');
    },

    notPremiseCompany() {
      if (!Array.isArray(this.contracts) || this.contracts.length === 0) return false;
      if (!this.isCompany) return true;
      const disAllowedTypes = ['LOK1', 'LOK2', 'LOK4'];
      return !this.contracts.some(contract =>
        contract.rentalObject?.rentalObjectTypeCode &&
        disAllowedTypes.includes(contract.rentalObject.rentalObjectTypeCode)
      );
    },

    getAddresses() {
      if (!!this.getErrorReportsList) {
        return uniqBy(this.getErrorReportsList.workorders, 'address');
      }

      return [];
    },

    getArchivedAddresses() {
      if (!!this.getArchivedErrorReportsList) {
        return uniqBy(this.getArchivedErrorReportsList.workorders, 'address');
      }

      return [];
    },

    getAddressOptions() {
      let options = {};

      if (!!this.getAddresses.length > 0) {
        this.getAddresses.forEach((address, i) => {
          options[i] = address.address;
        });
      }

      return options;
    },

    getArchivedAddressesOptions() {
      let options = {};

      if (!!this.getArchivedAddresses.length > 0) {
        this.getArchivedAddresses.forEach((address, i) => {
          options[i] = address.address;
        });
      }

      return options;
    },

    getFilteredErrorReport() {
      let errorList = [];
      if (this.getErrorReportsList.workorders) {
        if (!!this.addressFilter) {
          let _self = this;
          errorList = filter(this.getErrorReportsList.workorders, function (o) {
            return o.address.toLocaleLowerCase().indexOf(_self.addressFilter) !== -1;
          });
        } else {
          errorList = this.getErrorReportsList.workorders;
        }
      }
      return errorList;
    },

    getFilteredArchivedErrorReport() {
      let errorList = [];
      if (this.getArchivedErrorReportsList.workorders) {
        if (!!this.archivedAddressFilter) {
          let _self = this;
          errorList = filter(this.getArchivedErrorReportsList.workorders, function (o) {
            return o.address.toLocaleLowerCase().indexOf(_self.archivedAddressFilter) !== -1;
          });
        } else {
          errorList = this.getArchivedErrorReportsList.workorders;
        }
      }
      return errorList;
    },

    displayedArchivedWorkorders() {
      if (!this.getArchivedErrorReportsList?.workorders) return [];
      return this.showAllArchived
        ? this.getArchivedErrorReportsList.workorders
        : this.getArchivedErrorReportsList.workorders.slice(0, 3);
    }
  },
  created() {
    // Fetch error reports
    if (!this.testAlwaysLoading) {
      this.$store.cache.dispatch('fetchProfile').then((data) => {
        Promise.all([
          this.$store.cache.dispatch('fetchContracts'),
          this.$store.cache.dispatch('fetchErrorReportsList', data.contactCode),
       //   this.$store.cache.dispatch('fetchArchivedErrorReportsList', data.contactCode),
        ]).finally(() => {
          this.isLoading = false;
        });
      });
    }
  },
  methods: {
    showDate(date, format) {
      return dayjs(date).format(format);
    },
    fetchErrorReportDetails(item) {
      this.$store.cache.dispatch('fetchErrorReportsDetails', item.code).then((data) => {
        let result = filter(this.getErrorReportsList.workorders, {
          code: item.code,
        })[0];
        if (!result.accessCaption) {
          this.$store.cache.dispatch('updateErrorReportsDetails', item.code).finally(() => {});
        }
      });
    },

    mtm() {
      dataLayerPush({ 'event': 'serviceRequest', 'step': 'Start' });
    },


    fetchArchivedErrorReportDetails(item) {
      this.$store.cache.dispatch('fetchArchivedErrorReportDetails', item.code).then((data) => {});
    },

    filterOnAddress($event) {
      this.addressFilter = $event.target.value.toLocaleLowerCase();
    },

    filterOnArchivedAddress($event) {
      this.archivedAddressFilter = $event.target.value.toLocaleLowerCase();
    },

    trimStatus(statustext) {
      return statustext.replace(/\ -.*/, '');
    },

    fixAccessCaption(accessCaption) {
      return accessCaption === null ? 'Laddar tillträde...' : accessCaption;
    },

    isSameDate(d1, d2) {
      return dayjs(d1).format('YYYY-MM-DD HH:mm') == dayjs(d2).format('YYYY-MM-DD HH:mm');
    },
  },
};
</script>

